import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Trophy, Star, Coins, Heart, Sparkles, ArrowRight,
  Crown, AlertCircle, Plus, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin moderation hub for Plyx Stars.
 *
 * Two responsibilities on one page:
 *   1. Per-entry Judge's Pick toggle for any active talent contest.
 *   2. Season lifecycle controls — create a new season, advance to the
 *      next round (auto-picks top N), and end the season (awards trophy).
 *
 * Reachable from the AdminPlyxDashboard toolbar ("Plyx Stars").
 */
const AdminPlyxTalent = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  // Create-season form
  const [newSeasonBrand, setNewSeasonBrand] = useState("Plyx Stars · Season 1");
  const [newSeasonDesc, setNewSeasonDesc] = useState("");

  // Advance form (per active season)
  const [advanceChallengeIds, setAdvanceChallengeIds] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/plyx/admin/login", { replace: true, state: { from: "/admin/plyx/talent" } });
      return;
    }
    if (user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    load();
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hubR, seasonsR] = await Promise.all([
        api.get("/plyx/talent"),
        api.get("/admin/plyx/seasons"),
      ]);
      setHub(hubR.data);
      setSeasons(seasonsR.data.items || []);
      if (hubR.data.active?.length && !selectedChallengeId) {
        setSelectedChallengeId(hubR.data.active[0].challenge_id);
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [selectedChallengeId]);

  // Re-fetch entries whenever a challenge is selected.
  useEffect(() => {
    if (!selectedChallengeId) { setEntries([]); return; }
    api.get(`/plyx/challenges/${selectedChallengeId}`, { params: { entries_limit: 200 } })
      .then((r) => setEntries(r.data.entries || r.data.leaderboard || []))
      .catch(() => setEntries([]));
  }, [selectedChallengeId]);

  const togglePick = async (entryId, currentlyPicked) => {
    setBusy(entryId);
    try {
      if (currentlyPicked) {
        await api.delete(`/admin/plyx/talent/entries/${entryId}/judges-pick`);
        toast.success("Judge's Pick removed");
      } else {
        await api.post(`/admin/plyx/talent/entries/${entryId}/judges-pick`);
        toast.success("🏆 Judge's Pick set");
      }
      setEntries((prev) => prev.map((e) =>
        e.entry_id === entryId ? { ...e, is_judges_pick: !currentlyPicked } : e,
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const createSeason = async () => {
    if (!newSeasonBrand.trim()) { toast.error("Give the season a name"); return; }
    setBusy("create-season");
    try {
      const r = await api.post("/admin/plyx/seasons", {
        brand: newSeasonBrand.trim(),
        description: newSeasonDesc.trim() || null,
      });
      toast.success(`Season created — attach a talent challenge to round 1 to go live (id ${r.data.season_id})`);
      setNewSeasonBrand("Plyx Stars · Season 1");
      setNewSeasonDesc("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create season");
    } finally {
      setBusy(null);
    }
  };

  const attachRound = async (seasonId, roundIdx, challengeId) => {
    if (!challengeId) { toast.error("Pick a challenge first"); return; }
    setBusy(`attach-${seasonId}-${roundIdx}`);
    try {
      await api.post(`/admin/plyx/seasons/${seasonId}/attach-round`,
        null, { params: { challenge_id: challengeId, round_index: roundIdx } });
      toast.success("Round attached & live");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const advance = async (seasonId) => {
    const nextId = advanceChallengeIds[seasonId];
    if (!nextId) { toast.error("Paste the next round's challenge_id"); return; }
    setBusy(`advance-${seasonId}`);
    try {
      const r = await api.post(`/admin/plyx/seasons/${seasonId}/advance`,
        null, { params: { next_challenge_id: nextId } });
      toast.success(`Advanced to ${r.data.next_round} — ${r.data.survivors.length} survivors`);
      setAdvanceChallengeIds((p) => ({ ...p, [seasonId]: "" }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const endSeason = async (seasonId) => {
    if (!window.confirm("End this season? Top 3 will be locked in as winners (#1 gets the trophy badge).")) return;
    setBusy(`end-${seasonId}`);
    try {
      const r = await api.post(`/admin/plyx/seasons/${seasonId}/end`);
      toast.success(`Season ended · ${r.data.winners.length} winners crowned`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="container-narrow py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60"/></div>;
  }
  if (error) {
    return <div className="container-narrow py-20 text-center text-muted-foreground"><AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60"/>{error}</div>;
  }

  const liveSeason = seasons.find((s) => s.status === "live");

  return (
    <div className="container-narrow py-10" data-testid="admin-talent-page">
      <div className="mb-8">
        <div className="label-overline text-[#C05A3C] mb-2">Plyx · admin</div>
        <h1 className="font-display text-4xl">Plyx Stars control room</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Curate Judge&apos;s Picks on active contests and run multi-round seasons.
          See the public hub at <Link to="/plyx/talent" className="underline" data-testid="admin-talent-public-link">/plyx/talent</Link>.
        </p>
      </div>

      {/* ─── LIVE FINALS ─── */}
      <LiveFinalsCard seasons={seasons} />

      {/* ─── SEASONS ─── */}
      <section className="mb-12" data-testid="admin-talent-seasons-section">
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#FFB42D]"/> Seasons
        </h2>

        {/* Create form */}
        {!liveSeason && (
          <div className="mb-6 p-5 rounded-lg border border-black/10 bg-[#F3EFE9]/60" data-testid="admin-talent-create-season">
            <h3 className="font-semibold text-sm mb-3">Start a new season</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input
                value={newSeasonBrand}
                onChange={(e) => setNewSeasonBrand(e.target.value)}
                placeholder="Season name (e.g. Plyx Stars · Season 1)"
                data-testid="admin-talent-season-brand"
                className="px-3 py-2 rounded-md border border-black/15 bg-white text-sm"
              />
              <input
                value={newSeasonDesc}
                onChange={(e) => setNewSeasonDesc(e.target.value)}
                placeholder="Tagline (optional)"
                data-testid="admin-talent-season-desc"
                className="px-3 py-2 rounded-md border border-black/15 bg-white text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Defaults to 3 rounds: <strong>Auditions (30)</strong> → <strong>Semi-finals (12)</strong> → <strong>Finals (5)</strong>.
              After creating, attach an existing talent challenge to round 1.
            </p>
            <button
              type="button"
              onClick={createSeason}
              disabled={busy === "create-season"}
              data-testid="admin-talent-create-season-btn"
              className="px-4 py-2 rounded-md bg-[#C05A3C] hover:bg-[#D46B4E] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy === "create-season" ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
              Create season
            </button>
          </div>
        )}

        {/* Seasons list */}
        {seasons.length === 0 ? (
          <div className="p-6 rounded-lg border border-dashed border-black/15 text-sm text-muted-foreground text-center">
            No seasons yet. Start your first Plyx Stars season above.
          </div>
        ) : (
          <ul className="space-y-4" data-testid="admin-talent-seasons-list">
            {seasons.map((s) => {
              const cur = s.rounds[s.current_round];
              const isLive = s.status === "live";
              const isFinalRound = s.current_round === s.rounds.length - 1;
              return (
                <li key={s.season_id} data-testid={`admin-talent-season-${s.season_id}`} className="p-5 rounded-lg border border-black/10 bg-white">
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <div className="font-display text-lg">{s.brand}</div>
                      {s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${
                      s.status === "live" ? "bg-[#00C896]/15 text-[#00A57A]" :
                      s.status === "ended" ? "bg-black/10 text-black/60" :
                      "bg-[#FFB42D]/15 text-[#A87013]"
                    }`}>{s.status}</span>
                  </div>

                  {/* Rounds strip */}
                  <ol className="flex flex-wrap gap-2 mb-4">
                    {s.rounds.map((r, idx) => (
                      <li key={idx} className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${
                        idx === s.current_round && isLive ? "bg-[#C05A3C] text-white" :
                        r.status === "ended" ? "bg-black/8 text-black/60" :
                        "bg-black/5 text-black/55"
                      }`}>
                        {r.status === "ended" && <Trophy className="w-3 h-3"/>}
                        {r.name} · {r.slot_count}
                        {idx < s.rounds.length - 1 && <ChevronRight className="w-3 h-3 opacity-50"/>}
                      </li>
                    ))}
                  </ol>

                  {/* Winners (if ended) */}
                  {s.status === "ended" && s.winners?.length > 0 && (
                    <div className="mt-3 p-3 rounded-md bg-[#FFB42D]/8 border border-[#FFB42D]/20">
                      <div className="text-[11px] uppercase tracking-wider font-bold text-[#A87013] mb-2">Winners</div>
                      <ol className="space-y-1">
                        {s.winners.map((w) => (
                          <li key={w.plyx_user_id} className="text-xs flex items-center gap-2">
                            <span className="font-bold w-4">#{w.rank}</span>
                            <span>@{w.handle} · {w.country || "—"}</span>
                            <span className="text-muted-foreground ml-auto">score {w.score}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Actions when live */}
                  {isLive && (
                    <div className="mt-3 pt-3 border-t border-black/8 space-y-2">
                      {!cur?.challenge_id ? (
                        <AttachRoundInline
                          seasonId={s.season_id}
                          roundIdx={s.current_round}
                          roundName={cur?.name}
                          onSubmit={(cid) => attachRound(s.season_id, s.current_round, cid)}
                          busy={busy === `attach-${s.season_id}-${s.current_round}`}
                        />
                      ) : isFinalRound ? (
                        <button
                          type="button"
                          onClick={() => endSeason(s.season_id)}
                          disabled={busy === `end-${s.season_id}`}
                          data-testid={`admin-talent-end-${s.season_id}`}
                          className="px-4 py-2 rounded-md bg-[#FFB42D] hover:brightness-95 text-[#0A0A0A] text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {busy === `end-${s.season_id}` ? <Loader2 className="w-4 h-4 animate-spin"/> : <Crown className="w-4 h-4"/>}
                          End season & crown winners
                        </button>
                      ) : (
                        <div className="flex flex-wrap gap-2 items-end">
                          <div className="flex-1 min-w-[220px]">
                            <label className="label-overline mb-1.5 block">Next round&apos;s challenge_id</label>
                            <input
                              value={advanceChallengeIds[s.season_id] || ""}
                              onChange={(e) => setAdvanceChallengeIds((p) => ({ ...p, [s.season_id]: e.target.value }))}
                              placeholder="chl_..."
                              data-testid={`admin-talent-advance-input-${s.season_id}`}
                              className="w-full px-3 py-2 rounded-md border border-black/15 bg-white text-sm font-mono"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => advance(s.season_id)}
                            disabled={busy === `advance-${s.season_id}`}
                            data-testid={`admin-talent-advance-${s.season_id}`}
                            className="px-4 py-2 rounded-md bg-[#C05A3C] hover:bg-[#D46B4E] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {busy === `advance-${s.season_id}` ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
                            Advance to {s.rounds[s.current_round + 1]?.name}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ─── JUDGE'S PICKS ─── */}
      <section data-testid="admin-talent-picks-section">
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#FFB42D]"/> Judge&apos;s Picks
        </h2>

        {(hub?.active || []).length === 0 ? (
          <div className="p-6 rounded-lg border border-dashed border-black/15 text-sm text-muted-foreground text-center">
            No active talent contests. Create a talent challenge first.
          </div>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-4">
              {hub.active.map((c) => (
                <button
                  type="button"
                  key={c.challenge_id}
                  onClick={() => setSelectedChallengeId(c.challenge_id)}
                  data-testid={`admin-talent-contest-tab-${c.challenge_id}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    selectedChallengeId === c.challenge_id
                      ? "bg-[#C05A3C] text-white border-[#C05A3C]"
                      : "border-black/15 text-foreground/70 hover:bg-black/5"
                  }`}
                >
                  {c.title} · {c.entry_count}
                </button>
              ))}
            </div>

            {entries.length === 0 ? (
              <div className="p-6 rounded-lg border border-dashed border-black/15 text-sm text-muted-foreground text-center">
                No entries yet in this contest.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="admin-talent-entries-list">
                {entries.map((e) => (
                  <li
                    key={e.entry_id}
                    data-testid={`admin-talent-entry-${e.entry_id}`}
                    className={`flex items-center gap-3 p-3 rounded-md border ${
                      e.is_judges_pick ? "border-[#FFB42D]/40 bg-[#FFB42D]/5" : "border-black/8 bg-white"
                    }`}
                  >
                    {e.thumbnail_url ? (
                      <img src={e.thumbnail_url} alt="" className="w-12 h-16 object-cover rounded-md shrink-0"/>
                    ) : (
                      <div className="w-12 h-16 rounded-md bg-black/5 flex items-center justify-center shrink-0">
                        <Star className="w-4 h-4 opacity-40"/>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">
                        #{e.rank} @{e.creator.handle} · {e.creator.country || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{e.caption || "—"}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex gap-3">
                        <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3"/>{e.hearts_count}</span>
                        <span className="inline-flex items-center gap-1"><Coins className="w-3 h-3"/>{e.gift_coins}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePick(e.entry_id, e.is_judges_pick)}
                      disabled={busy === e.entry_id}
                      data-testid={`admin-talent-toggle-pick-${e.entry_id}`}
                      className={`px-3 py-2 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${
                        e.is_judges_pick
                          ? "bg-[#FFB42D] text-[#0A0A0A] hover:brightness-95"
                          : "border border-black/15 text-foreground/80 hover:bg-black/5"
                      }`}
                    >
                      {busy === e.entry_id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trophy className="w-3.5 h-3.5"/>}
                      {e.is_judges_pick ? "Judge's Pick ✓" : "Mark as Judge's Pick"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
};

const AttachRoundInline = ({ seasonId, roundIdx, roundName, onSubmit, busy }) => {
  const [val, setVal] = useState("");
  return (
    <div className="flex flex-wrap gap-2 items-end" data-testid={`admin-talent-attach-${seasonId}-${roundIdx}`}>
      <div className="flex-1 min-w-[220px]">
        <label className="label-overline mb-1.5 block">{roundName} challenge_id</label>
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="chl_... (create a talent challenge first, then paste id here)"
          className="w-full px-3 py-2 rounded-md border border-black/15 bg-white text-sm font-mono"
        />
      </div>
      <button
        type="button"
        onClick={() => onSubmit(val.trim())}
        disabled={busy || !val.trim()}
        className="px-4 py-2 rounded-md bg-[#C05A3C] hover:bg-[#D46B4E] text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
        Attach & go live
      </button>
    </div>
  );
};

export default AdminPlyxTalent;

// ── LiveFinalsCard ──────────────────────────────────────────────────────
// Compact widget that lists currently-live Plyx streams and lets the admin
// tag/untag one as the official Finals broadcast of a season. Idempotent —
// re-tagging the same stream is a no-op.
const LiveFinalsCard = ({ seasons }) => {
  const [streams, setStreams] = useState([]);
  const [picks, setPicks] = useState({}); // room_name → season_id
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/plyx/live/active");
      setStreams(r.data?.streams || []);
    } catch (e) { /* swallow */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const mark = async (room) => {
    const sid = picks[room];
    if (!sid) { toast.error("Pick a season first"); return; }
    setBusy(`mark-${room}`);
    try {
      await api.post("/admin/plyx/live/finals", { room_name: room, season_id: sid });
      toast.success("Tagged as Finals");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not tag finals");
    } finally { setBusy(null); }
  };

  const startRecording = async (room) => {
    setBusy(`rec-${room}`);
    try {
      const r = await api.post(`/admin/plyx/live/${room}/recording/start`);
      toast.success(r.data?.already_recording ? "Already recording" : "Recording started");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start recording");
    } finally { setBusy(null); }
  };

  const stopRecording = async (room) => {
    setBusy(`recstop-${room}`);
    try {
      await api.post(`/admin/plyx/live/${room}/recording/stop`);
      toast.success("Recording stopped");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not stop recording");
    } finally { setBusy(null); }
  };

  const unmark = async (room) => {
    setBusy(`unmark-${room}`);
    try {
      await api.delete(`/admin/plyx/live/finals/${room}`);
      toast.success("Finals tag removed");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not untag");
    } finally { setBusy(null); }
  };

  if (!streams.length) {
    return (
      <section className="mb-10" data-testid="admin-live-finals-empty">
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FFB42D]"/> Live Finals
        </h2>
        <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-black/15 p-4 bg-[#F3EFE9]/40">
          No-one is live right now. When a finalist goes live (or you mirror a finals broadcast), tag it here so it pops on /plyx/live with the gold 🏆 FINALS badge.
        </p>
      </section>
    );
  }

  return (
    <section className="mb-10" data-testid="admin-live-finals-section">
      <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-[#FFB42D]"/> Live Finals
      </h2>
      <div className="space-y-3">
        {streams.map((s) => (
          <div
            key={s.room_name}
            className={`p-4 rounded-lg border ${s.is_finals ? "border-[#FFD700] bg-[#FFF8E7]" : "border-black/10 bg-white"}`}
            data-testid={`admin-live-row-${s.room_name}`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="font-semibold text-sm">{s.title}</p>
                <p className="text-xs text-muted-foreground">@{s.creator?.handle} · {s.viewer_count} watching {s.is_finals && <span className="ml-2 text-[#C05A3C] font-bold">· 🏆 TAGGED</span>}</p>
              </div>
              <Link to={`/plyx/live/${s.room_name}`} target="_blank" rel="noreferrer" className="text-xs underline text-muted-foreground">Open ↗</Link>
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="label-overline mb-1.5 block">Season</label>
                <select
                  value={picks[s.room_name] || s.season_id || ""}
                  onChange={(e) => setPicks((p) => ({ ...p, [s.room_name]: e.target.value }))}
                  data-testid={`admin-live-season-select-${s.room_name}`}
                  className="w-full px-3 py-2 rounded-md border border-black/15 bg-white text-sm"
                >
                  <option value="">— pick a season —</option>
                  {seasons.map((sn) => (
                    <option key={sn.season_id} value={sn.season_id}>{sn.brand}</option>
                  ))}
                </select>
              </div>
              {!s.is_finals ? (
                <button
                  type="button"
                  onClick={() => mark(s.room_name)}
                  disabled={busy === `mark-${s.room_name}`}
                  data-testid={`admin-live-mark-finals-${s.room_name}`}
                  className="px-4 py-2 rounded-md bg-[#FFB42D] hover:brightness-95 text-[#0A0A0A] text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {busy === `mark-${s.room_name}` ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trophy className="w-4 h-4"/>}
                  Mark as Finals
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => unmark(s.room_name)}
                  disabled={busy === `unmark-${s.room_name}`}
                  data-testid={`admin-live-unmark-finals-${s.room_name}`}
                  className="px-4 py-2 rounded-md bg-white border border-black/15 hover:bg-[#F3EFE9] text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {busy === `unmark-${s.room_name}` ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
                  Remove tag
                </button>
              )}
              {/* Recording controls */}
              {!s.recording_egress_id ? (
                <button
                  type="button"
                  onClick={() => startRecording(s.room_name)}
                  disabled={busy === `rec-${s.room_name}`}
                  data-testid={`admin-live-record-start-${s.room_name}`}
                  className="px-3 py-2 rounded-md bg-[#C05A3C] hover:bg-[#D46B4E] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                  title="Requires LiveKit Egress + S3 credentials in backend .env"
                >
                  {busy === `rec-${s.room_name}` ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "● REC"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => stopRecording(s.room_name)}
                  disabled={busy === `recstop-${s.room_name}`}
                  data-testid={`admin-live-record-stop-${s.room_name}`}
                  className="px-3 py-2 rounded-md bg-white border border-black/15 hover:bg-[#F3EFE9] text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {busy === `recstop-${s.room_name}` ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "■ Stop rec"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
